import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { loadMessages } from '@siteshowroom/i18n'
import { I18nProvider, useI18n } from '@siteshowroom/i18n/react'
import type { Messages, Namespace, TranslatorMode } from '@siteshowroom/i18n'
import { SOURCE_LOCALE, SUPPORTED_LOCALES, negotiateLocale } from '@siteshowroom/shared'
import type { Locale } from '@siteshowroom/shared'

/**
 * De i18n-schil van het dashboard.
 *
 * Alle koppeling met @siteshowroom/i18n zit met opzet in dit ene bestand:
 * routes en componenten gebruiken uitsluitend `useTranslate` en `useLocale`
 * hieruit. Wijzigt het gedeelde pakket van vorm, dan is dit het enige bestand
 * dat meeverandert.
 *
 * Volgorde van locale-bepaling (§17.8): expliciete keuze van de gebruiker
 * (localStorage) → voorkeur van de browser via `negotiateLocale` → DEFAULT_LOCALE.
 * Het dashboard heeft geen padprefix per taal; dat is een eis van de publieke
 * site, niet van een afgeschermde SPA.
 */

const LOCALE_STORAGE_KEY = 'siteshowroom.dashboard.locale'

/**
 * De namespaces die het dashboard nodig heeft.
 *
 * `amenities` staat erbij omdat het beschrijvingsscherm de namen van voorzieningen
 * toont. Die lijst wordt gedeeld met de gastensite — dezelfde sleutels, dezelfde
 * woorden — en hoort dus in een eigen namespace en niet in `guest`, die het
 * dashboard niet laadt (§17.34).
 *
 * Moet deze lijst veranderen, verander hem dan ook in `APP_NAMESPACES` in
 * `packages/shared`: daar leest de sleutelcontrole hem, en anders faalt die op iets
 * wat wél werkt of laat hij iets door wat niet werkt.
 */
const NAMESPACES: readonly Namespace[] = ['common', 'dashboard', 'amenities']

/**
 * De modus wordt hier expliciet doorgegeven in plaats van uit NODE_ENV afgeleid.
 * In de browser bestaat `process` niet, waardoor het pakket zelf altijd op
 * productiegedrag zou uitkomen — en dan werpt een ontbrekende vertaalsleutel niet
 * tijdens de ontwikkeling, precies wat §17.8 wél wil.
 */
const TRANSLATOR_MODE: TranslatorMode = import.meta.env.DEV ? 'development' : 'production'

/** Vertaalfunctie zoals het gedeelde pakket die levert. */
export type TranslateFn = ReturnType<typeof useI18n>['t']

interface LocaleSelection {
  readonly locale: Locale
  readonly locales: readonly Locale[]
  readonly setLocale: (next: Locale) => void
}

const LocaleSelectionContext = createContext<LocaleSelection | null>(null)

function isSupportedLocale(value: string): value is Locale {
  const supported: readonly string[] = SUPPORTED_LOCALES
  return supported.includes(value)
}

/** localStorage kan in privémodus gooien; een taalvoorkeur is dat risico niet waard. */
function readStoredLocale(): Locale | null {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored !== null && isSupportedLocale(stored)) {
      return stored
    }
  } catch {
    return null
  }
  return null
}

function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Bewust genegeerd: de keuze geldt dan alleen voor deze sessie.
  }
}

function resolveInitialLocale(): Locale {
  const stored = readStoredLocale()
  if (stored !== null) {
    return stored
  }
  return negotiateLocale(navigator.language, SUPPORTED_LOCALES, SOURCE_LOCALE)
}

/**
 * De catalogi zijn statisch gebundeld, dus laden is synchroon. Het resultaat
 * behoudt de namespace als bovenste sleutel; componenten gebruiken daarom
 * volledig gekwalificeerde sleutels als `dashboard.system.title`. Dat is dezelfde
 * conventie als in de publieke site, zodat een sleutel overal hetzelfde heet.
 */
type CatalogState =
  | { readonly phase: 'ready'; readonly messages: Messages }
  | { readonly phase: 'failed'; readonly message: string }

function loadCatalogs(locale: Locale): CatalogState {
  try {
    return { phase: 'ready', messages: loadMessages(locale, NAMESPACES) }
  } catch (cause: unknown) {
    return { phase: 'failed', message: cause instanceof Error ? cause.message : String(cause) }
  }
}

export function AppI18nProvider({ children }: { children: ReactNode }): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(resolveInitialLocale)
  const state = useMemo<CatalogState>(() => loadCatalogs(locale), [locale])

  // Houdt <html lang> gelijk aan de actieve taal: nodig voor schermlezers,
  // afbreekregels en zoekmachines.
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((next: Locale): void => {
    writeStoredLocale(next)
    setLocaleState(next)
  }, [])

  const selection = useMemo<LocaleSelection>(
    () => ({ locale, locales: SUPPORTED_LOCALES, setLocale }),
    [locale, setLocale],
  )

  if (state.phase === 'failed') {
    // Opstartfout, geen UI-tekst: de melding is technisch en bewust onvertaald.
    return (
      <div className="app-boot app-boot--failed" role="alert">
        <p>Failed to load translations for locale &ldquo;{locale}&rdquo;.</p>
        <pre>{state.message}</pre>
      </div>
    )
  }

  return (
    <LocaleSelectionContext.Provider value={selection}>
      <I18nProvider locale={locale} messages={state.messages} mode={TRANSLATOR_MODE}>
        {children}
      </I18nProvider>
    </LocaleSelectionContext.Provider>
  )
}

/** Actieve taal plus de mogelijkheid die te wijzigen. */
export function useLocale(): LocaleSelection {
  const selection = useContext(LocaleSelectionContext)
  if (selection === null) {
    throw new Error('useLocale must be used inside <AppI18nProvider>')
  }
  return selection
}

/** Vertaalfunctie voor componenten; ICU-argumenten gaan als tweede parameter mee. */
export function useTranslate(): TranslateFn {
  return useI18n().t
}
